import { Alert, Box, Button, ButtonBase, Card, CardContent, Checkbox, Chip, CircularProgress, Collapse, Divider, IconButton, Stack, Typography } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SvgCloseIcon, addShowSeasons, addTitle, appAction, effectiveMonitorFuture, effectiveSelectedSeasons, prettyDateTime, refreshTitleSeasons, seasonDetails, titleStatus, titleStatusDesc } from "./helpers";
import { pages } from "./pages";
import { useState } from "react";
import { TitleCommon } from "./ListTitle";
import { STRING_SHOW_INFO, STRING_TITLE_ADD, STRING_TITLE_ERROR } from "./strings";

const ModalTitle = ({ item, dispatch }) => {
    const title = item;
    const status = titleStatus(title);
    const statusDesc = titleStatusDesc(title);
    const isShow = title.title_type === 'SHOW';
    const [working, setWorking] = useState(false);
    const [showSeasonSelector, setShowSeasonSelector] = useState(false);
    const [seasonSelectorTitle, setSeasonSelectorTitle] = useState(null);
    const [monitoredSeasonsOpen, setMonitoredSeasonsOpen] = useState(false);
    const hideTitleDetails = isShow && monitoredSeasonsOpen;

    const openSeasonSelector = () => {
        setWorking(true)
        refreshTitleSeasons(title.imdb_id)
            .then(response => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error()
                }
            })
            .then(response => {
                dispatch({ type: appAction.updateSearchTitle, title: response.title })
                setSeasonSelectorTitle(response.title)
                setShowSeasonSelector(true)
            })
            .catch(() => {
                window.alert('Sorry, something went wrong!')
            })
            .finally(() => {
                setWorking(false)
            })
    }

    if (isShow && status === 'NONE' && showSeasonSelector) {
        return (
            <SeasonSelectionModal
                title={seasonSelectorTitle ?? title}
                dispatch={dispatch}
                working={working}
                setWorking={setWorking}
                onBack={() => {
                    setShowSeasonSelector(false)
                    setSeasonSelectorTitle(null)
                }}
            />
        )
    }

    const addMovie = () => {
        setWorking(true)
        addTitle(title.imdb_id)
            .then(response => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error()
                }
            })
            .then(response => {
                dispatch({ type: appAction.updateSearchTitle, title: response.title })
                dispatch({ type: appAction.update, value: { currentPage: pages.downloads.id, selectedTitle: null, modalItem: null, modalType: null } })
            })
            .catch(() => {
                window.alert('Sorry, something went wrong!')
            })
            .finally(() => {
                setWorking(false)
            })
    }

    const actionLabel = isShow && status === 'NONE'
        ? 'Select seasons'
        : (statusDesc || STRING_TITLE_ADD);
    const actionWorking = working && status === 'NONE';
    return (
        <Card sx={titleLargeCardStyle}>
            <TitleLargeExitButton onClick={() => { window.history.go(-1) }} />
            <TitleCommon title={title} bannerSize={110} popMenu />
            <CardContent sx={{ p: 0, maxHeight: 'calc(100vh - 260px)', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ position: 'sticky', top: 0, zIndex: 3, bgcolor: 'background.paper', p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Button
                        variant="contained"
                        fullWidth
                        disabled={status !== 'NONE' || working}
                        startIcon={actionWorking ? <CircularProgress size={16} color="inherit" /> : null}
                        onClick={() => {
                            if (isShow) {
                                openSeasonSelector()
                            } else {
                                addMovie()
                            }
                        }}
                    >
                        {actionWorking ? 'Loading...' : actionLabel}
                    </Button>
                </Box>
                <Stack direction="column" spacing={2} sx={{ overflowY: 'auto', p: 2 }}>
                    {title.pending_info?.status === 'ERROR' &&
                        <Alert severity="error">
                            {STRING_TITLE_ERROR}
                        </Alert>
                    }
                    {isShow && ['HAVE', 'PENDING'].includes(status) &&
                        <MonitoredSeasonsEditor
                            title={title}
                            dispatch={dispatch}
                            working={working}
                            setWorking={setWorking}
                            open={monitoredSeasonsOpen}
                            setOpen={setMonitoredSeasonsOpen}
                        />
                    }
                    {!hideTitleDetails && title.title_type === 'SHOW' &&
                        <Alert severity="info">
                            {STRING_SHOW_INFO}
                        </Alert>
                    }
                    {title.x_status === 'HAVE' && title.x_complete_time &&
                        <Typography variant="subtitle2" color="text.secondary">
                            Added {prettyDateTime(title.x_complete_time, true)}
                        </Typography>
                    }
                    {!hideTitleDetails && title.rating &&
                        <Typography variant="body1">
                            {formatRating(title.rating)} / 10
                        </Typography>
                    }
                    {title.runtime &&
                        <Typography variant="body1">
                            {title.runtime} minutes
                        </Typography>
                    }
                    {!hideTitleDetails &&
                        <Typography variant="body2">
                            {title.description}
                        </Typography>
                    }
                </Stack>
            </CardContent>
        </Card>
    )
}

const SeasonSelectionModal = ({ title, dispatch, working, setWorking, onBack }) => {
    const [selectedSeasons, setSelectedSeasons] = useState([]);
    const [monitorFuture, setMonitorFuture] = useState(true);
    const canAdd = monitorFuture || selectedSeasons.length > 0;

    const addShow = () => {
        setWorking(true)
        addTitle(title.imdb_id, { seasons: selectedSeasons, monitorFuture, title })
            .then(response => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error()
                }
            })
            .then(response => {
                dispatch({ type: appAction.updateSearchTitle, title: response.title })
                dispatch({ type: appAction.update, value: { currentPage: pages.downloads.id, selectedTitle: null, modalItem: null, modalType: null } })
            })
            .catch(() => {
                window.alert('Sorry, something went wrong!')
            })
            .finally(() => {
                setWorking(false)
            })
    }

    return (
        <Card sx={titleLargeCardStyle}>
            <TitleLargeExitButton onClick={() => { window.history.go(-1) }} />
            <TitleCommon title={title} bannerSize={110} popMenu />
            <CardContent sx={{ p: 0, maxHeight: 'calc(100vh - 260px)', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ position: 'sticky', top: 0, zIndex: 3, bgcolor: 'background.paper', p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <IconButton size="small" onClick={onBack} disabled={working}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Button
                            variant="contained"
                            fullWidth
                            disabled={working || !canAdd}
                            onClick={addShow}
                        >
                            {STRING_TITLE_ADD}
                        </Button>
                    </Stack>
                </Box>
                <Stack direction="column" spacing={1.25} sx={{ overflowY: 'auto', p: 2 }}>
                    <Stack spacing={0.75}>
                        <Typography variant="h6">Select seasons</Typography>
                        <Alert
                            severity="warning"
                            sx={{
                                py: 0,
                                alignItems: 'center',
                                '& .MuiAlert-message': { py: 0.5 },
                                '& .MuiAlert-icon': { py: 0.5 },
                            }}
                        >
                            You can add more seasons at any time
                        </Alert>
                    </Stack>
                    <SeasonCheckboxList
                        title={title}
                        selectedSeasons={selectedSeasons}
                        setSelectedSeasons={setSelectedSeasons}
                        monitorFuture={monitorFuture}
                        setMonitorFuture={setMonitorFuture}
                        showBulkActions={false}
                    />
                </Stack>
            </CardContent>
        </Card>
    )
}

const SeasonCheckboxList = ({
    title,
    selectedSeasons,
    setSelectedSeasons,
    monitorFuture,
    setMonitorFuture,
    lockedSeasons = [],
    lockedFuture = false,
    showBulkActions = true,
}) => {
    const seasons = seasonDetails(title);
    const lockedSeasonSet = new Set(lockedSeasons);
    const selectedSeasonSet = new Set(selectedSeasons);
    const checkedSeason = (season) => lockedSeasonSet.has(season) || selectedSeasonSet.has(season);

    const setSeason = (season, checked) => {
        const next = new Set(selectedSeasons);
        if (checked) {
            next.add(season);
        } else {
            next.delete(season);
        }
        setSelectedSeasons([...next].sort((a, b) => a - b));
    }

    const selectAll = () => {
        setSelectedSeasons(seasons
            .map(season => season.season_number)
            .filter(season => !lockedSeasonSet.has(season))
            .sort((a, b) => a - b));
        if (!lockedFuture) {
            setMonitorFuture(true);
        }
    }

    const selectNone = () => {
        setSelectedSeasons([]);
        if (!lockedFuture) {
            setMonitorFuture(false);
        }
    }

    return (
        <Stack spacing={1}>
            <Box sx={{ maxHeight: 260, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1 }}>
                <SeasonCheckboxRow
                    checked={lockedFuture || monitorFuture}
                    disabled={lockedFuture}
                    onChange={(checked) => setMonitorFuture(checked)}
                    title="Future episodes"
                    subtitle="New episodes"
                />
                {seasons.map(season => (
                    <SeasonCheckboxRow
                        key={season.season_number}
                        checked={checkedSeason(season.season_number)}
                        disabled={lockedSeasonSet.has(season.season_number)}
                        onChange={(checked) => setSeason(season.season_number, checked)}
                        title={`Season ${season.season_number}`}
                        subtitle={seasonSubtitle(season)}
                        current={season.current}
                    />
                ))}
            </Box>
            {showBulkActions &&
                <Stack direction="row" spacing={1}>
                    <Button size="small" onClick={selectAll}>Select all</Button>
                    <Button size="small" onClick={selectNone}>Select none</Button>
                </Stack>
            }
        </Stack>
    )
}

const SeasonCheckboxRow = ({ checked, disabled, onChange, title, subtitle, current = false }) => {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
            <Checkbox
                checked={checked}
                disabled={disabled}
                onChange={(event) => onChange(event.target.checked)}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body1">{title}</Typography>
                    {current &&
                        <Chip
                            label="Current"
                            size="small"
                            color="primary"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                    }
                </Stack>
                {subtitle &&
                    <Typography variant="caption" color="text.secondary">
                        {subtitle}
                    </Typography>
                }
            </Box>
        </Box>
    )
}

const seasonSubtitle = (season) => {
    const parts = [];
    if (season.year) {
        parts.push(String(season.year));
    }
    if (season.air_date) {
        parts.push(`${formatSeasonDate(season.air_date)} premiere`);
    }
    if (season.episode_count) {
        parts.push(`${season.episode_count} episodes`);
    }
    return parts.join(' - ') || 'Year unknown'
}

const formatSeasonDate = (value) => {
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
        return value
    }
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

const formatRating = (rating) => {
    const value = Number(rating);
    if (Number.isNaN(value)) {
        return rating
    }
    return value.toFixed(1)
}

const MonitoredSeasonsEditor = ({ title, dispatch, working, setWorking, open, setOpen }) => {
    const [loadingSeasons, setLoadingSeasons] = useState(false);
    const [selectedSeasons, setSelectedSeasons] = useState([]);
    const [monitorFuture, setMonitorFuture] = useState(false);
    const monitoredSeasons = effectiveSelectedSeasons(title);
    const monitoredFuture = effectiveMonitorFuture(title);
    const canSave = selectedSeasons.length > 0 || (monitorFuture && !monitoredFuture);

    const toggleOpen = () => {
        if (open) {
            setOpen(false)
            return
        }

        setWorking(true)
        setLoadingSeasons(true)
        refreshTitleSeasons(title.imdb_id)
            .then(response => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error()
                }
            })
            .then(response => {
                dispatch({ type: appAction.updateSearchTitle, title: response.title })
                dispatch({ type: appAction.update, value: { modalItem: response.title }, history: false })
                setOpen(true)
            })
            .catch(() => {
                window.alert('Sorry, something went wrong!')
            })
            .finally(() => {
                setLoadingSeasons(false)
                setWorking(false)
            })
    }

    const save = () => {
        setWorking(true)
        addShowSeasons(title.imdb_id, { seasons: selectedSeasons, monitorFuture, title })
            .then(response => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error()
                }
            })
            .then(response => {
                dispatch({ type: appAction.updateSearchTitle, title: response.title })
                dispatch({ type: appAction.update, value: { modalItem: response.title }, history: false })
                setSelectedSeasons([])
                setMonitorFuture(false)
            })
            .catch(() => {
                window.alert('Sorry, something went wrong!')
            })
            .finally(() => {
                setWorking(false)
            })
    }

    return (
        <Stack spacing={1}>
            <Divider />
            <ButtonBase
                onClick={toggleOpen}
                disabled={working}
                sx={{
                    width: '100%',
                    bgcolor: 'action.hover',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    justifyContent: 'space-between',
                    px: 1,
                    py: 0.75,
                    textAlign: 'left',
                    '&:hover': {
                        bgcolor: 'action.selected',
                    },
                    '&.Mui-disabled': {
                        opacity: 0.7,
                    },
                }}
            >
                <Typography variant="subtitle1">Monitored seasons</Typography>
                {loadingSeasons
                    ? <CircularProgress size={20} />
                    : open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ButtonBase>
            <Collapse in={open}>
                <Stack spacing={2}>
                    <SeasonCheckboxList
                        title={title}
                        selectedSeasons={selectedSeasons}
                        setSelectedSeasons={setSelectedSeasons}
                        monitorFuture={monitorFuture}
                        setMonitorFuture={setMonitorFuture}
                        lockedSeasons={monitoredSeasons}
                        lockedFuture={monitoredFuture}
                        showBulkActions={false}
                    />
                    <Button
                        variant="contained"
                        disabled={working || !canSave}
                        onClick={save}
                    >
                        Save
                    </Button>
                </Stack>
            </Collapse>
        </Stack>
    )
}

const TitleLargeExitButton = ({ onClick }) => {
    return (
        <div
            style={{
                position: 'absolute',
                width: '30px',
                height: '30px',
                top: 5,
                right: 5,
                zIndex: 20,
                cursor: 'pointer',
            }}
            onClick={onClick}
        >
            <SvgCloseIcon />
        </div>
    )
}
const titleLargeCardStyle = {
    position: 'absolute',
    top: '50px',
    left: '50%',
    transform: 'translate(-50%, 0)',
    maxWidth: '85vw',
    width: '500px',
    boxShadow: 24,
}

export default ModalTitle;
